import { FC } from 'react';

import { Alert, Button, ButtonStack } from '@hh.ru/magritte-ui';
import { InfoCircleFilledSize24 } from '@hh.ru/magritte-ui/icon';
import { Link as SPALink } from '@hh.ru/redux-spa-middleware';

import useTrls from 'hooks/translation/useTrls';

import {
    CampaignFrontendStatus,
    setBudgetExceededAlert,
    setCampaignIdsForBudgetIncrease,
    setIsBudgetIncreaseModalVisible,
} from 'store/models/campaignsDashboard';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    title: 'CampaignBudgetExceededAlert.title',
    titleDailyBudgetExceeded: 'CampaignBudgetExceededAlert.title.dailyBudgetExceeded',
    description: 'CampaignBudgetExceededAlert.description',
    increaseBudget: 'CampaignBudgetExceededAlert.button.increaseBudget',
    cancel: 'CampaignBudgetExceededAlert.button.cancel',
};

export const CampaignBudgetExceededAlert: FC = () => {
    const statusChangeCampaignId = useSelector((state) => state.campaignsDashboard.statusChangeCampaignId);
    const isAlertVisible = useSelector((state) => state.campaignsDashboard.isBudgetExceededAlertVisible);
    const campaign = useSelector((state) => state.campaignsDashboard.campaigns);
    const frontendStatus = statusChangeCampaignId ? campaign.get(statusChangeCampaignId)?.frontendStatus : null;

    const isDailyBudgetExceeded = frontendStatus === CampaignFrontendStatus.DailyBudgetExceedStatus;

    const dispatch = useDispatch();
    const trls = useTrls();

    const handleClose = () => dispatch(setBudgetExceededAlert({ isVisible: false, campaignId: null }));

    const handleIncreaseBudget = () => {
        if (statusChangeCampaignId) {
            dispatch([
                setBudgetExceededAlert({ isVisible: false, campaignId: null }),
                setIsBudgetIncreaseModalVisible(true),
                setCampaignIdsForBudgetIncrease(new Set([statusChangeCampaignId])),
            ]);
        }
    };

    const primaryButton = isDailyBudgetExceeded ? (
        <Button
            mode="primary"
            style="accent"
            size="small"
            Element={SPALink}
            target="_blank"
            to={`/new/campaign/${statusChangeCampaignId ?? ''}?step=1`}
            onClick={handleClose}
            data-qa="alert-campaign-budget-exceeded-edit"
        >
            {trls(TrlKeys.increaseBudget)}
        </Button>
    ) : (
        <Button
            mode="primary"
            style="accent"
            size="small"
            onClick={handleIncreaseBudget}
            data-qa="alert-campaign-budget-exceeded-increase"
        >
            {trls(TrlKeys.increaseBudget)}
        </Button>
    );

    return (
        <Alert
            visible={isAlertVisible}
            title={isDailyBudgetExceeded ? trls(TrlKeys.titleDailyBudgetExceeded) : trls(TrlKeys.title)}
            description={trls(TrlKeys.description)}
            buttons={
                <ButtonStack stretched layout="vertical">
                    {primaryButton}
                    <Button mode="secondary" style="accent" onClick={handleClose}>
                        {trls(TrlKeys.cancel)}
                    </Button>
                </ButtonStack>
            }
            onClose={handleClose}
        >
            <InfoCircleFilledSize24
                initialColor="tertiary"
                backgroundStyle="secondary"
                padding={12}
                borderRadius="halfHeight"
            />
        </Alert>
    );
};
